/**
 * `workflowSpeechLanguage.ts` — the language a learner asked Spark to speak
 * for one guided run (web-only; no Unity counterpart).
 *
 * It is RUN state, held by `WorkflowRunModel`, at two scopes (user direction,
 * 2026-10-20). "Switch to Spanish" holds for the rest of the run, until the
 * learner switches again. "Repeat this step in Spanish" holds for THAT step
 * only — its replay and the questions asked on it — and the next step goes
 * back to the run's language. Every run starts in the language the workflow
 * is WRITTEN in (its `language:` field, English when absent), and narration is
 * translated only when the language asked for differs from that one. Before
 * this the choice lived only in the conversation, while each step's narration
 * arrived as a fresh English `INTERNAL SAY:` under a base prompt that forbids
 * translating one — so the learner heard the next step in English on about
 * half the runs (Spark trace of 2026-10-11).
 *
 * The name is whatever the agent passed, tidied. There is deliberately no list
 * of supported languages: the realtime model speaks many. The code table below
 * only spells out the codes a workflow's `language:` field uses.
 */

/** Longer than any language name; anything past it is a sentence, not a name. */
const K_MAX_SPEECH_LANGUAGE_LENGTH = 40;

/**
 * English in the spellings an agent or a learner is likely to produce —
 * the name itself, its code, and what other languages call it — so "back to
 * inglés" resets rather than setting a language called "Inglés". Compared
 * folded: lower case, accents dropped.
 */
const K_ENGLISH_NAMES: ReadonlySet<string> = new Set([
  "english",
  "en",
  "en-us",
  "en-gb",
  "us english",
  "american english",
  "british english",
  "ingles",
  "anglais",
  "englisch",
  "inglese",
  "engels",
]);

/** Lower case with accents dropped, for comparing names: "Inglés" → "ingles". */
function foldName(name: string): string {
  return name.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

/**
 * The common language CODES, as a workflow's `language:` field writes them
 * (the editor's picker offers these) and as an agent sometimes passes one.
 * A code not listed here is used as written.
 */
const K_LANGUAGE_CODE_NAMES: Readonly<Record<string, string>> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  nl: "Dutch",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  ar: "Arabic",
  hi: "Hindi",
  ru: "Russian",
  pl: "Polish",
  tr: "Turkish",
};

/**
 * A language name as the agent passed it, tidied for the prompt: trimmed,
 * inner whitespace collapsed, first letter upper-cased, a known code spelled
 * out ("es" → "Spanish"), and any spelling of English made `"English"`. Null
 * when nothing usable was passed.
 */
export function normalizeSpeechLanguage(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  if (name.length === 0 || name.length > K_MAX_SPEECH_LANGUAGE_LENGTH) return null;
  if (K_ENGLISH_NAMES.has(foldName(name))) return "English";
  const fromCode = K_LANGUAGE_CODE_NAMES[foldName(name).split(/[-_]/)[0] ?? ""];
  if (fromCode && name.length <= 5) return fromCode;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * The language a workflow is WRITTEN in, from its `language:` field — what
 * the step lines are spoken in when nobody asked for another. Absent or
 * unreadable means English, as the template seeds `en`.
 */
export function workflowLanguageName(code: string | null | undefined): string {
  return normalizeSpeechLanguage(code ?? "") ?? "English";
}

/** Whether two language names (or codes) name the same language: "Spanish" / "es" / "spanish". */
export function isSameLanguage(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  const na = normalizeSpeechLanguage(a);
  const nb = normalizeSpeechLanguage(b);
  return na !== null && nb !== null && foldName(na) === foldName(nb);
}

/** Whether a language is one OTHER than English — what the step-move word check asks. */
export function isNonEnglishSpeechLanguage(language: string | null): language is string {
  return language !== null && !isSameLanguage(language, "English");
}
